import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { DistrictKnowledgeBaseAPIResponse } from 'src/app/common/dtos/district.dto';
import { S3Object } from '../../dtos/s3-file-manager.dto';
import { AlertService, AlertType } from '../../services/alert/alert.service';
import { GlaiKnowledgebaseService } from '../../services/glai/glai-knowledgebase.service';
import { S3FileManagerService } from '../../services/s3-file-manager/s3-file-manager.service';
import { S3CreateFolderModalComponent } from '../s3-create-folder-modal/s3-create-folder-modal.component';
import { S3FileUploadModalComponent } from '../s3-file-upload-modal/s3-file-upload-modal.component';

interface S3DisplayItem {
  key: string;
  size: string;
  last_modified: string;
  isFolder: boolean;
  depth: number;
}

@Component({
  selector: 'app-s3-file-explorer',
  standalone: false,
  templateUrl: './s3-file-explorer.component.html',
  styleUrl: './s3-file-explorer.component.scss',
})
export class S3FileExplorerComponent implements OnChanges {
  @Input() knowledgeBase: DistrictKnowledgeBaseAPIResponse | null = null;

  @Output() readonly knowledgeBaseRefresh = new EventEmitter<void>();

  @ViewChild(S3FileUploadModalComponent)
  uploadModal: S3FileUploadModalComponent;

  @ViewChild(S3CreateFolderModalComponent)
  createFolderModal: S3CreateFolderModalComponent;

  s3ListItems: S3Object[] = [];

  currentPath = '';

  private history: string[] = [''];

  private historyIndex = 0;

  selectedItems: S3DisplayItem[] = [];

  expandedFolders = new Set<string>();

  dragOverFolderKey: string | null = null;

  dragOverRoot = false;

  isDragging = false;

  isExternalDragging = false;

  downloadState: 'idle' | 'loading' | 'done' = 'idle';

  isBusy = false;

  private lastClickedKey: string | null = null;

  private lastClickTime = 0;

  private lastClickedIndex = -1;

  readonly getDisplayName = S3FileExplorerComponent.getDisplayName;

  readonly getFileIcon = S3FileExplorerComponent.getFileIcon;

  readonly formatFileSize = S3FileExplorerComponent.formatFileSize;

  readonly formatDate = S3FileExplorerComponent.formatDate;

  readonly trackByKey = S3FileExplorerComponent.trackByKey;

  constructor(
    private alertService: AlertService,
    private s3FileManagerService: S3FileManagerService,
    private glaiKnowledgebaseService: GlaiKnowledgebaseService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['knowledgeBase'];
    if (!change) return;
    const prev = change.previousValue as
      | DistrictKnowledgeBaseAPIResponse
      | null
      | undefined;
    const curr = change.currentValue as
      | DistrictKnowledgeBaseAPIResponse
      | null
      | undefined;
    if (curr?.s3_bucket) {
      this.s3FileManagerService.setBucket(curr.s3_bucket);
      if (curr.s3_bucket !== prev?.s3_bucket) {
        this.getS3FileManagerList();
      }
    }
  }

  getS3FileManagerList(): void {
    this.isBusy = true;
    this.s3FileManagerService.listObjects().subscribe({
      next: (response) => {
        this.s3ListItems = response;
        this.isBusy = false;
      },
      error: () => {
        this.isBusy = false;
        this.alertService.showAlert('Error fetching S3 list');
      },
    });
  }

  onFilesChanged(): void {
    this.getS3FileManagerList();
    this.knowledgeBaseRefresh.emit();
  }

  get visibleItems(): S3DisplayItem[] {
    return this.buildTreeItems(this.currentPath, 0);
  }

  private getNewestDateInFolder(folderKey: string): string {
    return this.s3ListItems
      .filter((i) => i.key.startsWith(folderKey) && !i.key.endsWith('/'))
      .reduce<string>(
        (latest, i) => (i.last_modified > latest ? i.last_modified : latest),
        ''
      );
  }

  private static sortFoldersFirst(a: S3DisplayItem, b: S3DisplayItem): number {
    if (a.isFolder && !b.isFolder) return -1;
    if (!a.isFolder && b.isFolder) return 1;
    return a.key.localeCompare(b.key);
  }

  private buildFlatItems(path: string, depth: number): S3DisplayItem[] {
    const seen = new Set<string>();
    const items: S3DisplayItem[] = [];
    this.s3ListItems
      .filter((item) => item.key.startsWith(path) && item.key !== path)
      .forEach((item) => {
        const relative = item.key.slice(path.length);
        const slashIdx = relative.indexOf('/');
        if (slashIdx === -1) {
          items.push({
            key: item.key,
            size: item.size,
            last_modified: item.last_modified,
            isFolder: false,
            depth,
          });
        } else {
          const folderKey = path + relative.slice(0, slashIdx + 1);
          if (!seen.has(folderKey)) {
            seen.add(folderKey);
            items.push({
              key: folderKey,
              size: '0',
              last_modified: this.getNewestDateInFolder(folderKey),
              isFolder: true,
              depth,
            });
          }
        }
      });
    return items;
  }

  private buildTreeItems(path: string, depth: number): S3DisplayItem[] {
    const items = this.buildFlatItems(path, depth);
    items.sort(S3FileExplorerComponent.sortFoldersFirst);
    const result: S3DisplayItem[] = [];
    items.forEach((item) => {
      result.push(item);
      if (item.isFolder && this.expandedFolders.has(item.key)) {
        this.buildTreeItems(item.key, depth + 1).forEach((child) =>
          result.push(child)
        );
      }
    });
    return result;
  }

  get canGoBack(): boolean {
    return this.historyIndex > 0;
  }

  get canGoForward(): boolean {
    return this.historyIndex < this.history.length - 1;
  }

  get currentFolderName(): string {
    if (!this.currentPath) return 'Home';
    const trimmed = this.currentPath.slice(0, -1);
    const lastSlash = trimmed.lastIndexOf('/');
    return lastSlash === -1 ? trimmed : trimmed.slice(lastSlash + 1);
  }

  get parentPath(): string {
    if (!this.currentPath) return '';
    const trimmed = this.currentPath.slice(0, -1);
    const lastSlash = trimmed.lastIndexOf('/');
    return lastSlash === -1 ? '' : trimmed.substring(0, lastSlash + 1);
  }

  get parentFolderName(): string {
    if (!this.parentPath) return 'Home';
    const trimmed = this.parentPath.slice(0, -1);
    const lastSlash = trimmed.lastIndexOf('/');
    return lastSlash === -1 ? trimmed : trimmed.slice(lastSlash + 1);
  }

  get hasSelection(): boolean {
    return this.selectedItems.length > 0;
  }

  get canDropToCurrentLevel(): boolean {
    return this.selectedItems.some((item) => {
      const key = item.isFolder ? item.key.slice(0, -1) : item.key;
      const lastSlash = key.lastIndexOf('/');
      const parentPath =
        lastSlash === -1 ? '' : key.substring(0, lastSlash + 1);
      return parentPath !== this.currentPath;
    });
  }

  get hasSelectedFolder(): boolean {
    return this.selectedItems.some((i) => i.isFolder);
  }

  get selectedDownloadableFiles(): S3DisplayItem[] {
    return this.selectedItems.filter((i) => !i.isFolder);
  }

  isItemSelected(key: string): boolean {
    return this.selectedItems.some((s) => s.key === key);
  }

  static trackByKey(_index: number, item: S3DisplayItem): string {
    return item.key;
  }

  toggleFolder(key: string): void {
    const next = new Set(this.expandedFolders);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedFolders = next;
  }

  onDragStart(event: DragEvent, item: S3DisplayItem): void {
    if (!this.isItemSelected(item.key)) {
      this.selectedItems = [item];
      this.lastClickedIndex = -1;
    }
    event.dataTransfer?.setData('text/plain', 'drag');
    // Defer so the DOM doesn't mutate during the dragstart event itself
    setTimeout(() => {
      this.isDragging = true;
    }, 0);
  }

  onDragOver(event: DragEvent, item: S3DisplayItem): void {
    if (!item.isFolder) return;
    const wouldDropIntoSelf = this.selectedItems.some(
      (s) => item.key === s.key || item.key.startsWith(s.key)
    );
    if (wouldDropIntoSelf) return;
    event.preventDefault();
    this.dragOverFolderKey = item.key;
  }

  onDragLeave(event: DragEvent): void {
    const target = event.currentTarget as Element;
    if (!target.contains(event.relatedTarget as Node)) {
      this.dragOverFolderKey = null;
    }
  }

  onDragEnd(): void {
    this.dragOverFolderKey = null;
    this.dragOverRoot = false;
    this.isDragging = false;
  }

  onExternalDragOver(event: DragEvent): void {
    if (this.isDragging) return;
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.isExternalDragging = true;
  }

  onExternalDragLeave(event: DragEvent): void {
    const target = event.currentTarget as Element;
    if (!target.contains(event.relatedTarget as Node)) {
      this.isExternalDragging = false;
    }
  }

  async onExternalDrop(event: DragEvent): Promise<void> {
    if (this.isDragging) return;
    event.preventDefault();
    event.stopPropagation();
    this.isExternalDragging = false;
    if (!event.dataTransfer?.items.length) return;
    const entries = Array.from(event.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => entry !== null);
    const allFileEntries = (
      await Promise.all(entries.map((entry) => this.readFolderEntry(entry, '')))
    ).flat();
    if (allFileEntries.length) {
      this.uploadModal.openWithEntries(allFileEntries);
    }
  }

  private static readAllDirEntries(
    reader: FileSystemDirectoryReader
  ): Promise<FileSystemEntry[]> {
    return new Promise((resolve) => {
      const accumulated: FileSystemEntry[] = [];
      const read = () => {
        reader.readEntries((batch) => {
          if (!batch.length) {
            resolve(accumulated);
          } else {
            batch.forEach((e) => accumulated.push(e));
            read();
          }
        });
      };
      read();
    });
  }

  private async readFolderEntry(
    entry: FileSystemEntry,
    basePath: string
  ): Promise<{ file: File; subPath: string }[]> {
    if (entry.isFile) {
      return new Promise<{ file: File; subPath: string }[]>((resolve) => {
        (entry as FileSystemFileEntry).file((file) => {
          resolve([{ file, subPath: basePath }]);
        });
      });
    }
    const dirEntry = entry as FileSystemDirectoryEntry;
    const children = await S3FileExplorerComponent.readAllDirEntries(
      dirEntry.createReader()
    );
    const nested = await Promise.all(
      children.map((child) =>
        this.readFolderEntry(child, `${basePath}${entry.name}/`)
      )
    );
    return nested.flat();
  }

  onDragOverRoot(event: DragEvent): void {
    event.preventDefault();
    this.dragOverRoot = true;
  }

  onDragLeaveRoot(event: DragEvent): void {
    const target = event.currentTarget as Element;
    if (!target.contains(event.relatedTarget as Node)) {
      this.dragOverRoot = false;
    }
  }

  onDropToRoot(event: DragEvent): void {
    event.preventDefault();
    this.dragOverRoot = false;
    this.isDragging = false;
    const destination = this.canDropToCurrentLevel
      ? this.currentPath
      : this.parentPath;
    this.moveItemsTo(destination);
  }

  onDrop(event: DragEvent, targetFolder: S3DisplayItem): void {
    event.preventDefault();
    this.dragOverFolderKey = null;
    this.isDragging = false;
    if (!targetFolder.isFolder) return;
    const wouldDropIntoSelf = this.selectedItems.some(
      (s) => targetFolder.key === s.key || targetFolder.key.startsWith(s.key)
    );
    if (wouldDropIntoSelf) return;
    this.moveItemsTo(targetFolder.key);
  }

  moveItemsTo(destinationFolderKey: string): void {
    const folders = this.selectedItems.filter((i) => i.isFolder);
    const files = this.selectedItems.filter((i) => !i.isFolder);
    const actions: Observable<unknown>[] = [
      ...folders.map((f) =>
        this.s3FileManagerService.moveFolder(f.key, destinationFolderKey)
      ),
      ...(files.length
        ? [
            this.s3FileManagerService.moveFiles(
              files.map((f) => f.key),
              destinationFolderKey
            ),
          ]
        : []),
    ];
    this.isBusy = true;
    forkJoin(actions).subscribe({
      next: () => {
        this.isBusy = false;
        this.selectedItems = [];
        this.onFilesChanged();
      },
      error: () => {
        this.isBusy = false;
        this.alertService.showAlert('Error moving items');
      },
    });
  }

  navigateTo(item: S3DisplayItem): void {
    if (item.isFolder) {
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(item.key);
      this.historyIndex = this.history.length - 1;
      this.currentPath = item.key;
      this.selectedItems = [];
      this.lastClickedIndex = -1;
    }
  }

  private isDoubleClick(key: string, now: number): boolean {
    return this.lastClickedKey === key && now - this.lastClickTime < 300;
  }

  private handleDoubleClick(item: S3DisplayItem): void {
    if (item.isFolder) this.navigateTo(item);
    this.lastClickedKey = null;
  }

  private selectRange(anchorIndex: number, clickedIndex: number): void {
    const from = Math.min(anchorIndex, clickedIndex);
    const to = Math.max(anchorIndex, clickedIndex);
    this.selectedItems = this.visibleItems.slice(from, to + 1);
  }

  private handleSingleClick(item: S3DisplayItem, index: number): void {
    this.lastClickedIndex = index;
    const alreadyAlone =
      this.selectedItems.length === 1 && this.selectedItems[0].key === item.key;
    this.selectedItems = alreadyAlone ? [] : [item];
  }

  onRowClick(event: MouseEvent, item: S3DisplayItem, index: number): void {
    const now = Date.now();
    if (!event.shiftKey && this.isDoubleClick(item.key, now)) {
      this.handleDoubleClick(item);
      return;
    }
    this.lastClickedKey = item.key;
    this.lastClickTime = now;
    if (event.shiftKey && this.lastClickedIndex >= 0) {
      this.selectRange(this.lastClickedIndex, index);
    } else {
      this.handleSingleClick(item, index);
    }
  }

  downloadSelectedFiles(): void {
    const files = this.selectedDownloadableFiles;
    if (!files.length) return;
    if (files.length === 1) {
      this.downloadFile(files[0].key);
    } else {
      this.downloadFilesZip(files.map((f) => f.key));
    }
  }

  downloadFile(key: string): void {
    this.downloadState = 'loading';
    this.s3FileManagerService.downloadFile(key).subscribe({
      next: (response) => {
        const byteString = atob(response.content);
        const bytes = new Uint8Array(
          Array.from(byteString).map((ch) => ch.charCodeAt(0))
        );
        const blob = new Blob([bytes], { type: response.type });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = key.split('/').pop() ?? key;
        anchor.click();
        URL.revokeObjectURL(url);
        this.downloadState = 'done';
        this.alertService.showAlert(
          'File Downloaded',
          undefined,
          AlertType.PRIMARY
        );
        setTimeout(() => {
          this.downloadState = 'idle';
        }, 2000);
      },
      error: () => {
        this.downloadState = 'idle';
        this.alertService.showAlert('Error downloading file');
      },
    });
  }

  downloadFilesZip(keys: string[]): void {
    this.downloadState = 'loading';
    this.s3FileManagerService.downloadFiles(keys).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'download.zip';
        anchor.click();
        URL.revokeObjectURL(url);
        this.downloadState = 'done';
        this.alertService.showAlert(
          'Files Downloaded',
          undefined,
          AlertType.PRIMARY
        );
        setTimeout(() => {
          this.downloadState = 'idle';
        }, 2000);
      },
      error: () => {
        this.downloadState = 'idle';
        this.alertService.showAlert('Error downloading files');
      },
    });
  }

  navigateBack(): void {
    if (!this.canGoBack) return;
    this.historyIndex -= 1;
    this.currentPath = this.history[this.historyIndex];
    this.selectedItems = [];
    this.lastClickedIndex = -1;
  }

  navigateForward(): void {
    if (!this.canGoForward) return;
    this.historyIndex += 1;
    this.currentPath = this.history[this.historyIndex];
    this.selectedItems = [];
    this.lastClickedIndex = -1;
  }

  deleteSelectedItems(popover: { close: () => void }): void {
    if (!this.selectedItems.length) return;
    popover.close();
    const folders = this.selectedItems.filter((i) => i.isFolder);
    const files = this.selectedItems.filter((i) => !i.isFolder);
    const actions: Observable<unknown>[] = [
      ...folders.map((f) => this.s3FileManagerService.deleteFolder(f.key)),
      ...(files.length
        ? [this.s3FileManagerService.deleteFiles(files.map((f) => f.key))]
        : []),
    ];
    this.isBusy = true;
    forkJoin(actions).subscribe({
      next: (responses) => {
        this.isBusy = false;
        if (files.length) {
          const fileResponse = responses[responses.length - 1] as {
            failed?: string[];
          };
          if (fileResponse?.failed?.length) {
            this.alertService.showAlert(
              `Failed to delete: ${fileResponse.failed.join(', ')}`
            );
          }
        }
        this.selectedItems = [];
        this.onFilesChanged();
      },
      error: () => {
        this.isBusy = false;
        this.alertService.showAlert('Error deleting items');
      },
    });
  }

  static isFolder(key: string): boolean {
    return key.endsWith('/');
  }

  static getDisplayName(key: string): string {
    const trimmed = key.endsWith('/') ? key.slice(0, -1) : key;
    const parts = trimmed.split('/');
    return parts[parts.length - 1] || key;
  }

  static getFileIcon(key: string): string {
    if (S3FileExplorerComponent.isFolder(key)) {
      return 'bi-folder-fill';
    }
    const ext = key.split('.').pop()?.toLowerCase() ?? '';
    const iconMap: Record<string, string> = {
      png: 'bi-file-image',
      jpg: 'bi-file-image',
      jpeg: 'bi-file-image',
      gif: 'bi-file-image',
      svg: 'bi-file-image',
      mp4: 'bi-file-play',
      mov: 'bi-file-play',
      avi: 'bi-file-play',
      mkv: 'bi-file-play',
      mp3: 'bi-file-music',
      wav: 'bi-file-music',
      pdf: 'bi-file-pdf',
      doc: 'bi-file-word',
      docx: 'bi-file-word',
      xls: 'bi-file-earmark-spreadsheet',
      xlsx: 'bi-file-earmark-spreadsheet',
      csv: 'bi-file-earmark-spreadsheet',
      zip: 'bi-file-zip',
      gz: 'bi-file-zip',
      txt: 'bi-file-text',
      md: 'bi-file-text',
      json: 'bi-file-code',
      xml: 'bi-file-code',
      js: 'bi-file-code',
      ts: 'bi-file-code',
    };
    return iconMap[ext] ?? 'bi-file';
  }

  static formatFileSize(size: string): string {
    const bytes = parseInt(size, 10);
    if (Number.isNaN(bytes) || bytes === 0) return '--';
    const thresholds: [number, string][] = [
      [1024 * 1024 * 1024, 'GB'],
      [1024 * 1024, 'MB'],
      [1024, 'KB'],
    ];
    const match = thresholds.find(([divisor]) => bytes >= divisor);
    if (match) return `${(bytes / match[0]).toFixed(1)} ${match[1]}`;
    return `${bytes} bytes`;
  }

  static formatDate(isoDate: string): string {
    if (!isoDate) return '--';
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  forceSync(): void {
    if (!this.knowledgeBase) return;
    this.isBusy = true;
    this.glaiKnowledgebaseService
      .syncKnowledgeBase(this.knowledgeBase.district_id)
      .subscribe({
        next: () => {
          this.isBusy = false;
          this.alertService.showAlert(
            'Sync started successfully',
            undefined,
            AlertType.PRIMARY
          );
          this.knowledgeBaseRefresh.emit();
        },
        error: () => {
          this.isBusy = false;
          this.alertService.showAlert('Error starting sync');
        },
      });
  }
}
